import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../lib/api";

const CustomerAuthContext = createContext(null);

const TOKEN_KEY = "sabor-liquido-customer-token";

export const CustomerAuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(!!token);

    const apiAuth = useCallback(
        (path, options = {}) => {
            return axios({
                url: `${API}${path}`,
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    Authorization: token ? `Bearer ${token}` : undefined,
                    ...(options.headers || {}),
                },
            });
        },
        [token],
    );

    const refreshMe = useCallback(async () => {
        if (!token) return null;
        try {
            const { data } = await apiAuth("/auth/me");
            setUser(data);
            return data;
        } catch {
            localStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setUser(null);
            return null;
        }
    }, [token, apiAuth]);

    useEffect(() => {
        if (!token) {
            setLoading(false);
            return;
        }
        refreshMe().finally(() => setLoading(false));
    }, [token, refreshMe]);

    const login = async (email, password) => {
        const { data } = await axios.post(`${API}/auth/login`, { email, password });
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setToken(data.access_token);
        setUser({ id: data.id, email: data.email, name: data.name, role: "customer" });
        return data;
    };

    const register = async (payload) => {
        const { data } = await axios.post(`${API}/auth/register`, payload);
        localStorage.setItem(TOKEN_KEY, data.access_token);
        setToken(data.access_token);
        setUser({ id: data.id, email: data.email, name: data.name, role: "customer" });
        return data;
    };

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setUser(null);
    };

    return (
        <CustomerAuthContext.Provider
            value={{ token, user, loading, login, register, logout, apiAuth, refreshMe }}
        >
            {children}
        </CustomerAuthContext.Provider>
    );
};

export const useCustomerAuth = () => {
    const ctx = useContext(CustomerAuthContext);
    if (!ctx) throw new Error("useCustomerAuth must be used within CustomerAuthProvider");
    return ctx;
};
